<template>
  <el-dialog
    :title="title"
    :visible="visible"
    width="1100px"
    append-to-body
    :close-on-click-modal="false"
    @close="$emit('update:visible', false)"
  >
    <el-tabs
      v-if="type === 'package'"
      v-model="configType"
    >
      <el-tab-pane
        v-for="tab in packageTabs"
        :key="tab.name"
        :label="tab.label"
        :name="tab.name"
      />
    </el-tabs>

    <div
      v-loading="loading"
      class="detail-content"
    >
      <el-alert
        v-if="error"
        :title="error"
        type="error"
        :closable="false"
        show-icon
      >
        <el-button
          type="text"
          @click="load"
        >
          重新加载
        </el-button>
      </el-alert>

      <package-period-detail
        v-if="type === 'package' && data"
        :detail="data"
        :config-type="configType"
      />

      <sku-fixed-period-detail
        v-else-if="type === 'sku' && showFixedPeriod && data"
        :detail="data"
      />

      <template v-else-if="data">
        <el-descriptions
          :column="3"
          border
          size="small"
        >
          <el-descriptions-item label="编码">
            {{ data.productCode || data.skuCode }}
          </el-descriptions-item>
          <el-descriptions-item label="名称">
            {{ data.productName || data.skuName }}
          </el-descriptions-item>
          <el-descriptions-item label="发货周期">
            {{ periodText(data.finalCycle == null ? data.deliveryCycle : data.finalCycle) }}
          </el-descriptions-item>
          <el-descriptions-item
            v-if="data.versionNo"
            label="计算版本"
          >
            {{ data.versionNo }}
          </el-descriptions-item>
          <el-descriptions-item
            v-if="data.calculateTime"
            label="计算时间"
          >
            {{ data.calculateTime }}
          </el-descriptions-item>
          <el-descriptions-item
            v-if="data.logisticsModeName"
            label="发货方式"
          >
            {{ data.logisticsModeName }}
          </el-descriptions-item>
          <el-descriptions-item
            v-if="data.ruleVersion"
            label="规则版本"
          >
            {{ data.ruleVersion }}
          </el-descriptions-item>
        </el-descriptions>

        <el-alert
          v-if="data.exceptionMessage"
          :title="data.exceptionMessage"
          type="warning"
          :closable="false"
          class="detail-block"
        />
        <el-alert
          v-if="data.fallbackFlag === 1"
          :title="'已使用历史版本兜底：' + (data.sourceVersionNo || '—')"
          type="warning"
          :closable="false"
          class="detail-block"
        />

        <el-descriptions
          v-if="hasCycle(data.fixedCycle)"
          :column="3"
          border
          size="small"
          class="detail-block"
        >
          <el-descriptions-item label="自动周期">
            {{ periodText(data.autoCycle) }}
          </el-descriptions-item>
          <el-descriptions-item label="人工固定周期">
            {{ periodText(data.fixedCycle) }}
          </el-descriptions-item>
          <el-descriptions-item label="配置记录">
            {{ data.fixedConfigId || '—' }}
          </el-descriptions-item>
        </el-descriptions>

        <el-table
          :data="data.productPackList || []"
          border
          size="small"
          class="detail-block"
          empty-text="暂无产品及包件来源数据"
        >
          <el-table-column
            prop="productCode"
            label="产品编码"
            min-width="140"
          />
          <el-table-column
            prop="productName"
            label="产品名称"
            min-width="160"
          />
          <el-table-column
            label="产品周期"
            min-width="150"
          >
            <template slot-scope="scope">
              {{ periodText(scope.row.productDeliveryCycle) }}
            </template>
          </el-table-column>
          <el-table-column
            prop="packCode"
            label="包件编码"
            min-width="140"
          />
          <el-table-column
            prop="packName"
            label="包件名称"
            min-width="160"
          />
          <el-table-column
            label="包件周期"
            min-width="180"
          >
            <template slot-scope="scope">
              {{ periodText(scope.row.packDeliveryCycle) }}
            </template>
          </el-table-column>
        </el-table>
      </template>

      <el-empty
        v-else-if="!loading && !error"
        description="暂无发货周期明细"
      />
    </div>

    <span slot="footer">
      <el-button @click="$emit('update:visible', false)">
        关闭
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import {
  periodText,
  hasCycle,
  errorText,
  detailParams,
  responseData
} from '../model'
import PackagePeriodDetail from './PackagePeriodDetail'
import SkuFixedPeriodDetail from './SkuFixedPeriodDetail'

export default {
  components: {
    PackagePeriodDetail,
    SkuFixedPeriodDetail
  },
  props: {
    visible: Boolean,
    type: {
      type: String,
      default: 'sku'
    },
    record: {
      type: Object,
      default: () => ({})
    },
    current: Boolean
  },

  data () {
    return {
      data: null,
      loading: false,
      error: '',
      requestId: 0,
      configType: 'BASIC',
      packageTabs: [
        {
          name: 'BASIC',
          label: '基础发货周期'
        },
        {
          name: 'ACTIVITY',
          label: '活动发货周期'
        }
      ]
    }
  },

  computed: {
    showFixedPeriod () {
      return this.type === 'sku' &&
        this.current &&
        this.data &&
        this.data.existsFixedSendCycle === true
    },

    title () {
      if (this.showFixedPeriod) {
        return 'SKU固定发货周期明细'
      }

      if (this.type === 'package') {
        return '包件发货周期计算明细'
      }

      if (this.type === 'product') {
        return '产品发货周期明细'
      }

      return 'SKU 发货周期明细'
    }
  },

  watch: {
    configType () {
      if (this.type === 'package') {
        this.load()
      }
    }
  },

  created () {
    this.load()
  },

  beforeDestroy () {
    this.requestId++
  },

  methods: {
    periodText,
    hasCycle,

    async loadPackageDetail () {
      return responseData(
        await this.$q.shippingPeriod.packageDetail({
          params: {
            ...detailParams(this.record, 'package'),
            configType: this.configType
          }
        })
      )
    },

    async load () {
      const id = ++this.requestId
      this.loading = true
      this.error = ''
      this.data = null

      try {
        let result

        if (this.type === 'package') {
          result = await this.loadPackageDetail()
        } else if (this.type === 'product') {
          const packs = responseData(
            await this.$q.shippingPeriod.productPacks({
              data: { productCode: this.record.productCode }
            })
          )
          result = {
            ...this.record,
            productPackList: (packs || []).map(pack => ({
              productCode: this.record.productCode,
              productName: this.record.productName,
              productDeliveryCycle: this.record.deliveryCycle,
              packCode: pack.packCode,
              packName: pack.packName,
              packDeliveryCycle: pack.deliveryCycle
            }))
          }
        } else if (this.current && this.type === 'sku') {
          const goodsData = responseData(
            await this.$q.shippingPeriod.goodsPacks({
              data: { goodsCode: this.record.skuCode }
            })
          )

          const goodsObject = Array.isArray(goodsData) || !goodsData
            ? {}
            : goodsData
          const productPackList = Array.isArray(goodsData)
            ? goodsData
            : goodsObject.productPackList ||
              goodsObject.goodsPacks ||
              goodsObject.records ||
              (Array.isArray(goodsObject.data) ? goodsObject.data : [])

          result = {
            ...this.record,
            ...goodsObject,
            productPackList
          }
        } else {
          result = responseData(
            await this.$q.shippingPeriod.skuDetail({
              params: detailParams(this.record, 'sku')
            })
          )
        }

        if (id === this.requestId) {
          this.data = result
        }
      } catch (error) {
        if (id === this.requestId) {
          this.error = errorText(error)
        }
      } finally {
        if (id === this.requestId) {
          this.loading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.detail-content {
  height: 600px;
  min-height: 160px;
  overflow: auto;
}

.detail-block {
  margin-top: 16px;
}
</style>
